/* a group of DotButtons share a ContentCard.
The ContentCard remains displayed for 200ms after moving off a DotButton to allow
 user time to roll mouse over ContentCard and click it to play video.
 When a DotButton is rolled over the styling of all other dots represents related or unrelated dots
 and these are connected by lines.
 Clicking a Dot Button or Dot Card sends a callback
*/
/*
NB elements Ids
svgLinesDynamic
dynamicDots
dotContainer
*/
import { initAudio, clearAudio } from "../../../../js/audio";
import { DotButton } from "../../../dotbutton/js/dot_button";
import { ContentCard } from "../../../../modules/contentcard/js/content_card";
import {
  initLines,
  drawBackgroundLines,
  drawDynamicLines,
  clearThemeConnectionLines,
} from "./lines";
// interfaces
import { VideoItem } from "../posters";
import { AllDots, Point, getThemeColour } from "../../data/map_data";
import { getPointFromId, getViewBox, getDotStyle } from "./getters";

enum dotState {
  DEFAULT = "default",
  RELATED = "related",
  UNRELATED = "unrelated",
}

let gDotButtonTimer: number;
let gCurrentPerson: VideoItem;
let gCard: ContentCard;
let gCurrentTheme: string = "all";
let gDotButtons: DotButton[] = [];

const setTheme = (theme: string) => {
  gCurrentTheme = theme;
};

const initDesktopUI = (
  people: VideoItem[],
  dotData: AllDots,
  buttonClickCallback: Function
) => {
  initDots(people, dotData, buttonClickCallback);
  drawBackgroundLines(dotData);
  initLines();
};

/* dot utils */
const initDots = (
  people: VideoItem[],
  dotData: AllDots,
  buttonClickCallback: Function
) => {
  const cardNodeEl: HTMLElement = document.getElementById("selectedCard")!;
  gCard = new ContentCard(
    cardNodeEl,
    () => onMouseoverCardHandler(),
    () => onMouseoutHandler(people),
    () => onClickHandler(gCurrentPerson, people, buttonClickCallback)
  );

  const dynamicDots: HTMLElement = document.getElementById("dynamicDots")!;
  people.forEach((person) => {
    const themeColours: string[] = person.themeIds.map((theme: string) =>
      getThemeColour(theme)
    );
    const thisButton: DotButton = new DotButton(
      themeColours,
      person.id,
      () => onMouseoverButtonHandler(person, people, dotData),
      () => onMouseoutHandler(people),
      () => onClickHandler(person, people, buttonClickCallback)
    );
    // attach button to container
    dynamicDots.appendChild(thisButton.buttonNode);
    // position button
    const thisPoint: Point = getPointFromId(person.id, dotData.points);
    thisButton.setPosition(thisPoint.x, thisPoint.y, 15);
    // add button to list
    gDotButtons[person.id] = thisButton;
  });
};

const renderDot = (id: string, state: dotState) => {
  const buttonEl: HTMLElement | null = document.getElementById(
    `dot-button--${id}`
  );
  if (buttonEl !== null) {
    buttonEl.className = "dot-button--default";
    if (state === dotState.RELATED) {
      buttonEl.classList.add("dot-button--related");
    } else if (state === dotState.UNRELATED) {
      buttonEl.classList.add("dot-button--unrelated");
    }
  }
};

/* content card */
const getContentCardChildren = (data: VideoItem) => {
  return `
    <div class="content-card content-card-bg--${data.id}">
      <div class="content-card-children__info-container">
        <div class="content-card-children__name">${data.name}</div>
        <div class="content-card-children__video-duration">${data.duration}</div>
        <div class="content-card-children__description">${data.description}</div>
      </div>
      <div class="content-card__play-icon"></div>
    </div>`;
};

const showCardForPerson = (selectedPerson: VideoItem, points: Point[]) => {
  const cardContent: string = getContentCardChildren(selectedPerson);
  const coords: Point = getPointFromId(selectedPerson.id, points);
  gCard.show(selectedPerson.id, cardContent, coords, getViewBox());
};

const clear = (people: VideoItem[], letAudioPlay: boolean = false) => {
  clearThemeConnectionLines();
  gCard.hide();
  people.forEach((person) => renderDot(person.id, dotState.DEFAULT));
  clearTimeout(gDotButtonTimer);
  clearAudio(letAudioPlay);
};

/* event handlers */
// NB: when there's a mouseclick event the focus event fires first
// so this function gets called twice from DotButton
const onMouseoverButtonHandler = (
  person: VideoItem,
  people: VideoItem[],
  dotData: AllDots
) => {
  clearTimeout(gDotButtonTimer);
  const selectedPerson: VideoItem = person;
  drawDynamicLines(selectedPerson, dotData, gCurrentTheme);

  // at this point create card
  gCurrentPerson = selectedPerson;
  showCardForPerson(selectedPerson, dotData.points);

  // style all dots based on whether they are related to this dotButton
  people.forEach((person) => {
    const dotStyle: dotState = getDotStyle(
      person,
      selectedPerson,
      gCurrentTheme
    );
    renderDot(person.id, dotStyle);
  });

  const audio: HTMLElement | null = document.getElementById(
    `${selectedPerson.id}_audio`
  );
  if (audio !== null) initAudio(audio as HTMLAudioElement);
};

// used by dot and card
const onClickHandler = (
  person: VideoItem,
  people: VideoItem[],
  buttonClickCallback: Function
) => {
  // set previousAudio to null so current audio can play out without being stopped by clear
  // this allows the audio to continue playing over the video
  const letAudioContinue = true;
  clear(people, letAudioContinue);

  buttonClickCallback({
    ACTION: "loadVideo",
    state: { videoId: person.video_id },
  });
};

// used by card
const onMouseoverCardHandler = () => {
  clearTimeout(gDotButtonTimer);
};

// used by dot and card
const onMouseoutHandler = (people: VideoItem[]) => {
  // Typescript returns type NodeJS.Timer for setTimeout
  // Need to use window to clarify that return type is number
  gDotButtonTimer = window.setTimeout(() => clear(people), 200);
};

const renderDesktopItem = (id: string, isDisplayed: boolean) => {
  const thisDotButton: DotButton = gDotButtons[id];
  thisDotButton && thisDotButton.setDisabledState(!isDisplayed);
};

export { initDesktopUI, setTheme, renderDesktopItem };
